import { EFinancingCompany } from '@if/core/types'
import DeleteIcon from '@mui/icons-material/Delete'
import {
  Button,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import TableCell, { tableCellClasses, TableCellProps } from '@mui/material/TableCell'
import { styled } from '@mui/material/styles'
import { useUpdateMerchantParent } from '@src/data/api/merchants-api/merchants-api'
import { Merchant } from '@src/data/types/Merchant'
import { UpdateParentDto } from '@src/data/types/UpdateParentDto'
import { ChangeEvent, MouseEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TableToolbar from '../../components/toolBar'
import SelectMerchantsDialog from './components/SelectMerchantsDialog'
import Footer from './components/footer'

interface InactiveProp extends TableCellProps {
  inactive?: boolean
}

const StyledTableCell = styled(TableCell, {
  shouldForwardProp: (prop) => prop !== 'inactive',
})<InactiveProp>(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: 'inherit',
  },
  variants: [
    {
      props: ({ inactive }) => inactive ?? false,
      style: {
        [`&.${tableCellClasses.body}`]: {
          color: theme.palette.text.disabled,
        },
      },
    },
  ],
}))

interface Props {
  banner: Merchant | null
  merchantsList: Merchant[]
  isFetchingMerchantsList: boolean
}

const Merchants = ({ banner, merchantsList, isFetchingMerchantsList }: Props) => {
  const { t } = useTranslation()

  const [openUserDialog, setOpenUserDialog] = useState(false)
  const [filteredValue, setFilteredValue] = useState<string>('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(5)

  const [updateMerchantParent] = useUpdateMerchantParent()

  const merchantsFiltered = useMemo(() => {
    if (!merchantsList) return []

    return merchantsList.filter(
      (merchant) =>
        merchant.id.toLowerCase().includes(filteredValue.toLowerCase()) ||
        merchant.name.toLowerCase().includes(filteredValue.toLowerCase()) ||
        merchant.province.toLowerCase().includes(filteredValue.toLowerCase()),
    )
  }, [merchantsList, filteredValue])

  const handleSearch = useCallback((value: string) => {
    setFilteredValue(value)
  }, [])

  const closeMerchantDialog = useCallback(() => {
    setOpenUserDialog(false)
  }, [])

  if (!banner) return null

  const handleMerchantsSelected = async (newMerchants: Merchant[]) => {
    setOpenUserDialog(false)

    const promises: Promise<Merchant | void>[] = []

    newMerchants.forEach((merchant) => {
      if (merchant) {
        const dto: UpdateParentDto = {
          parentId: banner.id,
          merchantId: merchant.id,
        }

        promises.push(updateMerchantParent(dto))
      }
    })

    await Promise.all(promises)
  }

  const deleteMerchant = async (id: string) => {
    const dto: UpdateParentDto = {
      parentId: '',
      merchantId: id,
    }

    await updateMerchantParent(dto)
  }

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - merchantsFiltered.length) : 0

  const handleChangePage = (_event: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10)
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  return (
    <>
      <TableContainer component={Paper}>
        <TableToolbar
          handleAdd={setOpenUserDialog}
          handleSearch={handleSearch}
          title={t('common.merchant')}
          addButtonTooltip={t('merchant.addMerchant')}
          disableAddButton={banner.financingCompanyId === EFinancingCompany.Iceberg}
        />

        <Table sx={{ maxHeight: '25rem' }} aria-label="custom pagination table">
          <TableHead>
            <TableRow>
              <StyledTableCell>#</StyledTableCell>
              <StyledTableCell>{t('common.fullName')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.language')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.province')}</StyledTableCell>
              <StyledTableCell align="right">{t('common.program')}</StyledTableCell>
              <StyledTableCell align="center">{t('common.website')}</StyledTableCell>
              <StyledTableCell align="right" />
            </TableRow>
          </TableHead>

          {isFetchingMerchantsList ? (
            <TableBody>
              <TableRow>
                <TableCell colSpan={7} sx={{ padding: 0 }}>
                  <LinearProgress sx={{ height: '10px' }} />
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {(!merchantsFiltered || merchantsFiltered.length === 0) && !isFetchingMerchantsList && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center' }}>
                    {t('common.noResultsFound')}
                  </TableCell>
                </TableRow>
              )}
              {(rowsPerPage > 0
                ? merchantsFiltered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : merchantsFiltered
              ).map((row) => {
                const inactive = !row.isActive

                return (
                  <TableRow key={row.id}>
                    <StyledTableCell inactive={inactive}>
                      {inactive ? (
                        <Typography variant="body2">{row.id}</Typography>
                      ) : (
                        <Typography variant="body2" component={Button} href={`/merchant/${row.id}/view`} color="info">
                          {row.id}
                        </Typography>
                      )}
                    </StyledTableCell>

                    <StyledTableCell inactive={inactive} align="left">
                      {row.name}
                    </StyledTableCell>

                    <StyledTableCell inactive={inactive} align="right">
                      {t(`ELanguage.${row.languageId}`, { ns: 'enum' })}
                    </StyledTableCell>

                    <StyledTableCell inactive={inactive} align="right">
                      {row.province}
                    </StyledTableCell>

                    <StyledTableCell inactive={inactive} align="right">
                      {row.supportedFinancingPrograms
                        .map((e) => t(`EFinancingProgram.${e}`, { ns: 'enum' }))
                        .join(', ')}
                    </StyledTableCell>

                    <StyledTableCell inactive={inactive} align="center">
                      <Typography
                        component="span"
                        sx={{
                          a: {
                            color: 'inherit',
                            textDecoration: 'none',
                            '&:hover': {
                              textDecoration: 'underline',
                            },
                          },
                        }}
                      >
                        <a href={row.website} target="_blank" rel="noopener noreferrer">
                          {row.website}
                        </a>
                      </Typography>
                    </StyledTableCell>

                    <StyledTableCell align="right">
                      <IconButton aria-label="remove merchant" onClick={() => deleteMerchant(row.id)}>
                        <DeleteIcon sx={{ color: 'red' }} />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                )
              })}

              {emptyRows > 0 && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell colSpan={7} />
                </TableRow>
              )}
            </TableBody>
          )}

          <TableFooter>
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 15, 25]}
                count={merchantsFiltered.length}
                rowsPerPage={rowsPerPage}
                page={page}
                slotProps={{
                  select: {
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true,
                  },
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={Footer}
              />
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
      <SelectMerchantsDialog
        open={openUserDialog}
        title={t('merchant.addMerchant')}
        label={t('common.merchant')}
        allowMultiple
        onConfirm={handleMerchantsSelected}
        onCancel={closeMerchantDialog}
      />
    </>
  )
}

export default Merchants
